// Typed client for the JSON API. A 401 sends the browser to the login page.

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public fields: Record<string, string> = {},
  ) {
    super(message);
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (res.status === 401) {
    window.location.href = '/auth/login';
    throw new ApiError(401, 'unauthenticated');
  }
  if (res.status === 204) {
    return undefined as T;
  }
  const data = (await res.json().catch(() => ({}))) as { error?: string; fields?: Record<string, string> };
  if (!res.ok) {
    throw new ApiError(res.status, data.error ?? res.statusText, data.fields ?? {});
  }
  return data as T;
}

async function upload<T>(path: string, form: FormData): Promise<T> {
  const res = await fetch(path, { method: 'POST', body: form });
  if (res.status === 401) {
    window.location.href = '/auth/login';
    throw new ApiError(401, 'unauthenticated');
  }
  const data = (await res.json().catch(() => ({}))) as { error?: string; fields?: Record<string, string> };
  if (!res.ok) {
    throw new ApiError(res.status, data.error ?? res.statusText, data.fields ?? {});
  }
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, body),
  delete: (path: string) => request<void>('DELETE', path),
  upload,
  /** Number of documents linked to each object of a kind, keyed by id. */
  documentCounts: (target: 'employee' | 'client' | 'contract' | 'bank_account' | 'bank_transaction') =>
    request<Record<string, number>>('GET', `/api/documents/counts?target=${target}`),
};

export interface Me {
  email: string;
  name: string;
  role: string;
}

export interface Employee {
  id: number;
  name: string;
  email: string;
  position: string | null;
  address: string | null;
  phone: string | null;
  embg: string | null;
  /** The employee's own account, 15 digits or IBAN; transfers to it are backed by the employee on import. */
  bank_account: string | null;
  start_date: string;
  end_date: string | null;
  employment_type: EmploymentType;
  /** Expected hours per working day, e.g. "7.50"; "0.00" opts out of shortfall alerts. */
  hours_per_day: string;
  created_at: string;
  updated_at: string;
}

export type EmploymentType = 'full_time' | 'part_time' | 'subcontractor';

export const employmentTypeLabels: Record<EmploymentType, string> = {
  full_time: 'Full-time',
  part_time: 'Part-time',
  subcontractor: 'Subcontractor',
};

export type EmployeeInput = Omit<Employee, 'id' | 'created_at' | 'updated_at' | 'hours_per_day'> & {
  /** Empty keeps the default of 7.5. */
  hours_per_day: string | null;
};

export interface Client {
  id: number;
  name: string;
  /** Legal name printed on invoices when it differs from name. */
  billing_name: string | null;
  /** The client's own reference / PO / cost centre, printed on invoices. */
  reference: string | null;
  billing_email: string | null;
  country: string;
  tax_id: string | null;
  is_domestic: boolean;
  default_currency: 'EUR' | 'MKD';
  street: string | null;
  postal_code: string | null;
  city: string | null;
  payment_days: number;
  /** Whether an Everhour API token is stored; the token itself is never returned. */
  everhour_token_set: boolean;
  /** Last characters of the stored token, to tell which one it is. */
  everhour_token_hint: string | null;
  created_at: string;
  updated_at: string;
}

/** Write-only `everhour_api_token`: omitted keeps the stored token, '' removes it, a value replaces it. */
export type ClientInput = Omit<Client, 'id' | 'created_at' | 'updated_at' | 'everhour_token_set' | 'everhour_token_hint'> & {
  everhour_api_token?: string;
};

export interface BankAccount {
  id: number;
  label: string;
  currency: 'EUR' | 'MKD';
  is_default: boolean;
  is_active: boolean;
  company_name: string;
  company_address: string;
  vat_number: string;
  contact_email: string;
  bank_name: string;
  bank_address: string;
  iban: string;
  swift: string;
  correspondent_bank_name: string | null;
  correspondent_swift: string | null;
  correspondent_currency: string | null;
  correspondent_account: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export type BankAccountInput = Omit<BankAccount, 'id' | 'created_at' | 'updated_at'>;

/** Formats a normalized IBAN in groups of four for display. */
export function formatIban(iban: string): string {
  return iban.replace(/\s+/g, '').replace(/(.{4})(?=.)/g, '$1 ');
}

export interface Period {
  start: string;
  end: string | null;
}

export interface Contract {
  id: number;
  employee_id: number;
  employee_name: string;
  client_id: number;
  client_name: string;
  period: Period;
  hourly_rate: string;
  currency: 'EUR' | 'MKD';
  kind: 'hourly' | 'guaranteed';
  guaranteed_hours_per_month: string | null;
  /** The developer's user ID in this client's Everhour workspace. */
  everhour_user_id: string | null;
  /** Billing group used when no routing rule matches (fallback) or always. */
  default_billing_group_id: number | null;
  billing_group_mode: 'fallback' | 'always';
  created_at: string;
  updated_at: string;
}

export interface ContractInput {
  employee_id: number;
  client_id: number;
  period: Period;
  hourly_rate: string;
  currency: string;
  kind: string;
  guaranteed_hours_per_month: string | null;
  everhour_user_id: string | null;
  default_billing_group_id: number | null;
  billing_group_mode: 'fallback' | 'always';
}

/** Result of POST /api/contracts/everhour-check: proof that a client's Everhour token works and which member an ID points at. */
export interface EverhourCheckResult {
  workspace: { id: number; name: string; key_owner: string; key_owner_email: string };
  member: {
    id: number;
    name: string;
    email: string;
    status: string;
    role: string;
    type: string;
    headline: string;
    /** null when no employee was given */
    email_matches_employee: boolean | null;
  } | null;
  activity: { from: string; to: string; hours: string; entries: number; last_entry: string | null } | null;
  candidates: { id: number; name: string; email: string; status: string }[];
}

// ---- Timesheets (Everhour import) ----

export interface BillingGroup {
  id: number | null;
  name: string;
}

export interface EverhourRule {
  id?: number;
  kind: 'attribute' | 'workspace';
  attribute: string | null;
  value: string | null;
  workspace: string | null;
  /** Group name, or null to ignore the match and keep looking. */
  group: string | null;
}

export interface BillingConfig {
  groups: BillingGroup[];
  rules: EverhourRule[];
}

export interface ImportSummary {
  entries: number;
  hours: string;
  added: number;
  changed: number;
  removed: number;
  unresolved: number;
  /** changed or removed entries that are already on an invoice */
  invoiced_changes: number;
  leave_days: number;
  leave_removed: number;
  holidays_added: number;
  unmatched_users: { id: number; name: string; entries: number; hours: string }[];
  contracts_without_everhour_id: string[];
}

export interface ImportRun {
  id: number;
  client_id: number;
  month: string;
  actor: string;
  started_at: string;
  finished_at: string | null;
  status: 'running' | 'done' | 'failed';
  error: string | null;
  summary: ImportSummary | null;
  changes: number;
}

export interface TimesheetLeaveDay {
  id: number;
  employee_id: number;
  date: string;
  kind: 'vacation' | 'sick' | 'other' | 'holiday';
  fraction: string;
  source: 'everhour' | 'manual';
  client_name: string | null;
  note: string | null;
}

export interface TimesheetDeveloper {
  contract_id: number;
  employee_id: number;
  name: string;
  everhour_user_id: string | null;
  hours_total: string;
  /** group id → hours */
  hours_by_group: Record<string, string>;
  unresolved_hours: string;
  /** date → hours */
  days: Record<string, string>;
  entries: number;
  leave: TimesheetLeaveDay[];
}

export interface TimesheetView {
  client_id: number;
  month: string;
  from: string;
  to: string;
  groups: { id: number; name: string }[];
  developers: TimesheetDeveloper[];
  last_run: ImportRun | null;
}

export interface TimeEntry {
  id: number;
  everhour_id: number;
  contract_id: number;
  employee_id: number;
  date: string;
  seconds: number;
  hours: string;
  task_id: string | null;
  task_name: string | null;
  task_number: string | null;
  task_url: string | null;
  projects: string[];
  attributes: Record<string, string>;
  comment: string | null;
  is_locked: boolean;
  billing_group_id: number | null;
  billing_group: string | null;
  resolved_by: string;
  removed: boolean;
  invoice_id: number | null;
  invoice_number?: string | null;
}

export interface ImportChange {
  id: number;
  kind: 'added' | 'changed' | 'removed';
  time_entry_id: number;
  everhour_id: number;
  employee_name: string;
  date: string;
  before: TimeEntry | null;
  after: TimeEntry | null;
}

export interface Holiday {
  date: string;
  name: string;
  source: 'everhour' | 'manual';
}

export interface VacationAnchor {
  id: number;
  employee_id: number;
  as_of: string;
  remaining_days: string;
  note: string | null;
}

export type InvoiceStatus = 'draft' | 'issued' | 'archived';

export interface Invoice {
  id: number;
  client_id: number;
  client_name: string;
  status: InvoiceStatus;
  number: string | null;
  period_from: string;
  period_to: string;
  invoice_date: string;
  due_date: string;
  currency: 'EUR' | 'MKD';
  bank_account_id: number | null;
  bank_account_label: string | null;
  reference: string | null;
  note: string | null;
  vat_rate: string;
  subtotal: string;
  vat_amount: string;
  total: string;
  document_id: number | null;
  ujp_status: 'none' | 'registered';
  generated_at: string | null;
  issued_at: string | null;
  issued_by: string | null;
  archived_at: string | null;
  created_by: string;
  created_at: string;
}

export interface InvoiceLine {
  id: number;
  section_id: number;
  position: number;
  kind: 'generated' | 'remaining' | 'manual';
  contract_id: number | null;
  employee_id: number | null;
  description: string;
  units: string;
  unit_price: string;
  amount: string;
  detail: Record<string, unknown> | null;
}

export interface InvoiceSection {
  id: number;
  position: number;
  title: string;
  kind: 'hours' | 'remaining' | 'custom';
  billing_group_id: number | null;
  lines: InvoiceLine[];
  units: string;
  subtotal: string;
}

export interface InvoiceWarning {
  kind: string;
  message: string;
}

export interface InvoiceDetail extends Invoice {
  sections: InvoiceSection[];
  warnings: InvoiceWarning[];
  /** the timesheets no longer match the generated lines */
  stale: boolean;
}

export const invoiceStatusLabels: Record<InvoiceStatus, string> = { draft: 'Draft', issued: 'Issued', archived: 'Archived' };

/** GET /api/leave/summary: one employee's leave in a range plus the balance from the latest anchor. */
export interface LeaveSummary {
  employee_id: number;
  name: string;
  from: string;
  to: string;
  /** vacation, sick and other leave in the range; holidays are left out */
  entries: TimesheetLeaveDay[];
  totals: Record<'vacation' | 'sick' | 'other', string>;
  anchors: VacationAnchor[];
  balance: { as_of: string; remaining: string; taken_since: string; balance: string } | null;
}

export interface EmployeeMonth {
  employee_id: number;
  name: string;
  employment_type: EmploymentType;
  hours_per_day: string;
  hours_total: string;
  hours_by_client: { client_id: number; client_name: string; hours: string }[];
  /** kind → days */
  leave: Record<string, string>;
  leave_days: TimesheetLeaveDay[];
  working_days: number;
  holidays: number;
  expected_hours: string | null;
  /** logged − expected */
  difference: string | null;
  vacation_balance: { as_of: string; remaining: string; taken_since: string; balance: string } | null;
}

export interface EmployeesView {
  month: string;
  from: string;
  to: string;
  is_previous_month: boolean;
  employees: EmployeeMonth[];
  holidays: { date: string; name: string }[];
}

export const leaveKindLabels: Record<TimesheetLeaveDay['kind'], string> = {
  vacation: 'Vacation',
  sick: 'Sick',
  other: 'Other leave',
  holiday: 'Holiday',
};

/** "2026-09" for a Date. */
export function monthKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export type AuditEntity = 'employee' | 'client' | 'contract' | 'bank_account' | 'document' | 'invoice' | 'bank_statement' | 'bank_transaction';

/** One row of GET /api/audit: who changed what, with API snapshots before and after. */
export interface AuditEntry {
  id: number;
  at: string;
  actor: string;
  action: 'create' | 'update' | 'delete' | 'upload' | string;
  entity: AuditEntity;
  entity_id: number;
  summary: string;
  before: unknown;
  after: unknown;
}

/** Reads a form into a plain object; empty strings become null. */
export function formValues(form: HTMLFormElement): Record<string, string | null> {
  const out: Record<string, string | null> = {};
  for (const [key, value] of new FormData(form).entries()) {
    const s = String(value).trim();
    out[key] = s === '' ? null : s;
  }
  return out;
}

export type DocumentType =
  | 'm2'
  | 'services_agreement'
  | 'outbound_invoice'
  | 'inbound_invoice'
  | 'receipt'
  | 'legal'
  | 'bank_statement'
  | 'other';

export const documentTypeLabels: Record<DocumentType, string> = {
  m2: 'M2 work contract',
  services_agreement: 'Services agreement',
  outbound_invoice: 'Outbound invoice',
  inbound_invoice: 'Inbound invoice',
  receipt: 'Receipt',
  legal: 'Legal',
  bank_statement: 'Bank statement',
  other: 'Other',
};

export type LinkKind = 'employee' | 'client' | 'contract' | 'bank_account' | 'bank_transaction';

export const linkKindLabels: Record<LinkKind, string> = {
  employee: 'employee',
  client: 'client',
  contract: 'contract',
  bank_account: 'account',
  bank_transaction: 'transaction',
};

export interface DocumentLink {
  kind: LinkKind;
  id: number;
  label: string;
}

export interface Document {
  id: number;
  type: DocumentType;
  title: string;
  document_date: string | null;
  reference: string | null;
  counterparty: string | null;
  amount: string | null;
  currency: 'EUR' | 'MKD' | null;
  notes: string | null;
  original_filename: string;
  content_type: string;
  size_bytes: number;
  sha256: string;
  uploaded_by: string;
  links: DocumentLink[];
  created_at: string;
  updated_at: string;
}

export interface DocumentLinkInput {
  employee_id?: number;
  client_id?: number;
  contract_id?: number;
  bank_account_id?: number;
  bank_transaction_id?: number;
}

export interface DocumentMetadata {
  type: string;
  title: string;
  document_date: string | null;
  reference: string | null;
  counterparty: string | null;
  amount: string | null;
  currency: string | null;
  notes: string | null;
  links: DocumentLinkInput[];
}

/** What the server read from a file about to be uploaded (POST /api/documents/extract). */
export interface ExtractResult {
  /** ocr+typesafe: fields proposed; ocr: text only; none: nothing readable. */
  source: 'ocr+typesafe' | 'ocr' | 'none';
  type: DocumentType | null;
  title: string | null;
  document_date: string | null;
  reference: string | null;
  counterparty: string | null;
  amount: string | null;
  currency: 'EUR' | 'MKD' | null;
  text: string;
  warnings: string[];
}

/** Accepted upload types, mirrored from the server allowlist. */
export const acceptedFileTypes = '.pdf,.png,.jpg,.jpeg,.heic,.docx,.xlsx,.csv,.txt,.mt940,.sta,.xml';

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// ---- Bank statements ----

export type BackingKind = 'document' | 'employee' | 'tax' | 'internal' | 'fee' | 'none';

export const backingKindLabels: Record<BackingKind, string> = {
  document: 'Document',
  employee: 'Employee contract',
  tax: 'Tax on another transfer',
  internal: 'Internal transfer',
  fee: 'Bank fee',
  none: 'Not needed',
};

export interface BankWarning {
  kind: string;
  message: string;
}

export interface BankStatement {
  id: number;
  bank_account_id: number;
  account_label: string;
  currency: 'EUR' | 'MKD';
  statement_number: string;
  statement_date: string;
  opening_balance: string;
  closing_balance: string;
  total_debits: string;
  total_credits: string;
  total_fees: string;
  debit_count: number;
  credit_count: number;
  source_filename: string;
  period_from: string | null;
  period_to: string | null;
  warnings: BankWarning[];
  document_id: number | null;
  document_filename: string | null;
  transaction_count: number;
  outstanding_count: number;
  imported_by: string;
  imported_at: string;
}

export interface AttachedDocument {
  id: number;
  type: DocumentType;
  title: string;
  original_filename: string;
  amount: string | null;
  currency: string | null;
}

export interface BankTransaction {
  id: number;
  statement_id: number;
  statement_number: string;
  statement_date: string;
  bank_account_id: number;
  account_label: string;
  currency: 'EUR' | 'MKD';
  seq: number;
  value_date: string;
  direction: 'debit' | 'credit';
  amount: string;
  fee: string;
  counterparty_account: string | null;
  counterparty_name: string | null;
  code: string | null;
  bank_reference: string | null;
  payment_reference: string | null;
  description: string;
  reference: string | null;
  backing_kind: BackingKind;
  backing_note: string | null;
  employee_id: number | null;
  employee_name: string | null;
  /** For a tax row: the transfer it belongs to (same payment reference) and that transfer in words. */
  tax_of_transaction_id: number | null;
  tax_of: string | null;
  /** Tax rows filed under this transfer. */
  tax_count: number;
  documents: AttachedDocument[];
  /** Waits for its backing: a document row without one, or a tax row whose transfer does. */
  outstanding: boolean;
  created_at: string;
  updated_at: string;
}

export interface BankStatementDetail extends BankStatement {
  transactions: BankTransaction[];
}

export interface BackingInput {
  backing_kind: BackingKind;
  backing_note: string | null;
  employee_id: number | null;
}

export interface ImportFilePreview {
  name: string;
  kind: 'csv' | 'pdf' | 'unknown';
  size_bytes: number;
  paired_with: string | null;
  error: string | null;
}

export interface ImportTransactionPreview {
  seq: number;
  value_date: string;
  direction: 'debit' | 'credit';
  amount: string;
  fee: string;
  counterparty_account: string;
  counterparty_name: string;
  code: string;
  bank_reference: string;
  payment_reference: string;
  description: string;
  reference: string;
  backing_kind: BackingKind;
  employee_id: number | null;
  employee_name: string | null;
  prefill_reason: string;
}

export interface ImportStatementPreview {
  key: string;
  file: string;
  pdf: string | null;
  iban: string;
  currency: string;
  account_holder: string;
  bank_account: { id: number; label: string; iban: string; currency: string } | null;
  statement_number: string;
  statement_date: string;
  opening_balance: string;
  closing_balance: string;
  total_debits: string;
  total_credits: string;
  total_fees: string;
  debit_count: number;
  credit_count: number;
  period_from: string | null;
  period_to: string | null;
  already_imported: boolean;
  existing_statement_id: number | null;
  errors: string[];
  warnings: BankWarning[];
  importable: boolean;
  transactions: ImportTransactionPreview[];
}

export interface ImportPreview {
  files: ImportFilePreview[];
  statements: ImportStatementPreview[];
}

export interface ImportSelection {
  statements: { key: string; pdf?: string | null }[];
}

export interface ImportResult {
  imported: { key: string; statement_id: number; transactions: number; pdf_document_id: number | null; pdf_error: string | null }[];
  skipped: { key: string; reason: string }[];
}
